// Fairness suggester for the Plan board. Assigns each UNASSIGNED slot the best
// candidate, chronologically, balancing load and never double-booking a guide
// across overlapping slots on the same day.
//
// Candidate tiers follow eligibility.ts (owner ruling 2026-07-17): ✨-preferred
// guides get first claim on a slot; if none can be placed, fall back to "open"
// guides (no marker, qualified). ❌ guides are never suggested. Each suggestion
// carries its tier so the UI can show whether the pick honors a preference or
// drafts someone who simply didn't respond.
//
// Ordering within a tier mirrors the legacy picker's fairness precedence (see
// picker.ts buildAllProposals): fewer shifts THAT day → fewer of THAT tour →
// fewer total this window → stable by name. Existing assignments seed the load
// maps so already-busy guides aren't piled on.

import type { GuideRef, PlanSlot } from "@/lib/load";
import { candidatesFor, productIdForTour } from "@/lib/eligibility";
import { guideByPeekId, lookupGuideByAnyName } from "@/config/guides";
import { slotStartMin } from "@/lib/dates";

const DEFAULT_DURATION_MIN = 90;

export interface Suggestion {
  guide: GuideRef;
  /** "preferred" = honors a ✨; "open" = fallback draft of an unmarked guide. */
  tier: "preferred" | "open";
}

/** pageId -> suggestion (null = nobody assignable could be placed). */
export type SuggestionMap = Record<string, Suggestion | null>;

interface Window {
  startMin: number;
  endMin: number;
}

/** Assigned in Notion by any signal — display names OR the reliable cmau_ id
 * (a Worker-synced assignment can carry the id with no People-prop name). */
function isAssigned(slot: PlanSlot): boolean {
  return slot.assignedGuideNames.length > 0 || !!slot.assignedGuidePeekId;
}

export function suggestAssignments(
  slots: PlanSlot[],
  staged: Record<string, GuideRef> = {},
): SuggestionMap {
  const totalLoad: Record<string, number> = {};
  const dayLoad: Record<string, Record<string, number>> = {};
  const tourLoad: Record<string, Record<string, number>> = {};
  const dayBusy: Record<string, Record<string, Window[]>> = {};

  const bump = (guideId: string, date: string, tour: string, win: Window | null) => {
    totalLoad[guideId] = (totalLoad[guideId] || 0) + 1;
    (dayLoad[date] ??= {})[guideId] = (dayLoad[date]?.[guideId] || 0) + 1;
    (tourLoad[guideId] ??= {})[tour] = (tourLoad[guideId]?.[tour] || 0) + 1;
    if (win) ((dayBusy[date] ??= {})[guideId] ??= []).push(win);
  };

  const busyAt = (guideId: string, date: string, win: Window): boolean => {
    const list = dayBusy[date]?.[guideId];
    return !!list && list.some((b) => b.startMin < win.endMin && win.startMin < b.endMin);
  };

  const windowOf = (slot: PlanSlot): Window | null => {
    const s = slotStartMin(slot);
    return s == null ? null : { startMin: s, endMin: s + DEFAULT_DURATION_MIN };
  };

  // Chronological so earlier slots get first pick (matches picker precedence).
  // True start time (label fallback) first — date-only/midnight rows must not
  // jump the queue ahead of real morning tours.
  const ordered = slots
    .slice()
    .sort(
      (a, b) =>
        a.date.localeCompare(b.date) ||
        (slotStartMin(a) ?? 9999) - (slotStartMin(b) ?? 9999) ||
        a.hhmm.localeCompare(b.hhmm),
    );

  // Seed load maps from what's already assigned (names + Peek id + the
  // operator's staged draft), so suggestions don't pile onto busy guides.
  for (const slot of ordered) {
    const seedIds = new Set<string>();
    for (const name of slot.assignedGuideNames) {
      const g = lookupGuideByAnyName(name);
      if (g) seedIds.add(g.id);
    }
    if (slot.assignedGuidePeekId) seedIds.add(slot.assignedGuidePeekId);
    const st = staged[slot.pageId];
    if (st) seedIds.add(st.id);
    for (const id of seedIds) bump(id, slot.date, slot.tour, windowOf(slot));
  }

  const fairest = (pool: GuideRef[], slot: PlanSlot): GuideRef | null => {
    if (pool.length === 0) return null;
    const sorted = pool.slice().sort((a, b) => {
      const ad = dayLoad[slot.date]?.[a.id] || 0;
      const bd = dayLoad[slot.date]?.[b.id] || 0;
      if (ad !== bd) return ad - bd;
      const at = tourLoad[a.id]?.[slot.tour] || 0;
      const bt = tourLoad[b.id]?.[slot.tour] || 0;
      if (at !== bt) return at - bt;
      const as = totalLoad[a.id] || 0;
      const bs = totalLoad[b.id] || 0;
      if (as !== bs) return as - bs;
      return a.peekName.localeCompare(b.peekName);
    });
    return sorted[0];
  };

  const suggestions: SuggestionMap = {};
  for (const slot of ordered) {
    // Only fill slots nobody is assigned to (Notion) or staged on (draft) yet.
    if (isAssigned(slot) || staged[slot.pageId]) continue;
    if (slot.excluded) {
      suggestions[slot.pageId] = null;
      continue;
    }
    const win = windowOf(slot);
    const { preferred, open } = candidatesFor(slot);
    const freeOf = (pool: GuideRef[]) =>
      pool.filter(
        (g) =>
          !guideByPeekId[g.id]?.inactive && // benched: manually assignable, never auto-drafted
          (!win || !busyAt(g.id, slot.date, win)), // busy = a real conflict, never fall back to it
      );

    let pick = fairest(freeOf(preferred), slot);
    let tier: Suggestion["tier"] = "preferred";
    // Open-tier fallback only when the tour label resolved to a real product —
    // on an unknown label the "qualified" pool is a market-wide guess, too weak
    // for auto-fill (manual staging stays available in the dropdown/grid).
    if (!pick && productIdForTour(slot.tour) != null) {
      pick = fairest(freeOf(open), slot);
      tier = "open";
    }
    if (!pick) {
      suggestions[slot.pageId] = null;
      continue;
    }
    suggestions[slot.pageId] = { guide: pick, tier };
    bump(pick.id, slot.date, slot.tour, win);
  }

  return suggestions;
}

/** Count of slots that got a concrete suggestion. */
export function suggestionCount(map: SuggestionMap): number {
  return Object.values(map).filter((s): s is Suggestion => s != null).length;
}
